import { Box, Button, Dialog, Flex, Portal, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { api } from '../api';
import { HostSite, WarehousePayload } from '../types';

const JsonFoldingEditor = lazy(() =>
  import('./JsonFoldingEditor').then(module => ({ default: module.JsonFoldingEditor }))
);

type WarehouseTabId = keyof WarehousePayload;
type TabKind = 'object' | 'array';

const WAREHOUSE_TABS: Array<{ id: WarehouseTabId; label: string; kind: TabKind }> = [
  { id: 'configuration', label: 'Config', kind: 'object' },
  { id: 'containerTypes', label: 'Container types', kind: 'array' },
  { id: 'layers', label: 'Layers', kind: 'array' },
  { id: 'stations', label: 'Stations', kind: 'array' },
  { id: 'baskets', label: 'Baskets', kind: 'array' },
  { id: 'robots', label: 'Robots', kind: 'array' },
  { id: 'rails', label: 'Rails', kind: 'array' },
  { id: 'racklights', label: 'Racklights', kind: 'object' },
  { id: 'locations', label: 'Locations', kind: 'array' },
  { id: 'carts', label: 'Carts', kind: 'array' },
];

function emptySection(tab: (typeof WAREHOUSE_TABS)[number]): unknown {
  if (tab.id === 'racklights') {
    return { station: [], cart: [] };
  }
  return tab.kind === 'object' ? {} : [];
}

function sectionValue(warehouse: WarehousePayload | undefined, tab: (typeof WAREHOUSE_TABS)[number]): unknown {
  const value = warehouse?.[tab.id];
  return value === undefined ? emptySection(tab) : value;
}

function sectionCount(value: unknown, kind: TabKind): number {
  if (kind === 'array' && Array.isArray(value)) {
    return value.length;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as { station?: unknown; cart?: unknown };
    if (Array.isArray(record.station) || Array.isArray(record.cart)) {
      const stationCount = Array.isArray(record.station) ? record.station.length : 0;
      const cartCount = Array.isArray(record.cart) ? record.cart.length : 0;
      return stationCount + cartCount;
    }
    return Object.keys(value).length;
  }
  return 0;
}

function displayedSection(
  tab: (typeof WAREHOUSE_TABS)[number],
  drafts: Record<WarehouseTabId, string> | null,
  warehouse: WarehousePayload | undefined
): unknown {
  if (drafts) {
    try {
      return JSON.parse(drafts[tab.id]) as unknown;
    } catch {
      return sectionValue(warehouse, tab);
    }
  }
  return sectionValue(warehouse, tab);
}

function draftsFromWarehouse(warehouse: WarehousePayload): Record<WarehouseTabId, string> {
  const drafts = {} as Record<WarehouseTabId, string>;
  for (const tab of WAREHOUSE_TABS) {
    drafts[tab.id] = JSON.stringify(sectionValue(warehouse, tab), null, 2);
  }
  return drafts;
}

function parseWarehouseDrafts(
  drafts: Record<WarehouseTabId, string>
): { warehouse: WarehousePayload } | { error: string } {
  const warehouse: WarehousePayload = {};
  for (const tab of WAREHOUSE_TABS) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(drafts[tab.id]);
    } catch (error) {
      return { error: `${tab.label}: ${error instanceof Error ? error.message : 'Invalid JSON'}` };
    }
    if (tab.kind === 'array' && !Array.isArray(parsed)) {
      return { error: `${tab.label} must be a JSON array` };
    }
    if (tab.kind === 'object' && (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))) {
      return { error: `${tab.label} must be a JSON object` };
    }
    (warehouse as Record<string, unknown>)[tab.id] = parsed;
  }
  return { warehouse };
}

export function SiteWarehouseConfig({ site, hostName }: { site: HostSite; hostName: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['host', hostName, 'site', site.site_name, 'warehouse'];
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<WarehouseTabId>('configuration');
  const [drafts, setDrafts] = useState<Record<WarehouseTabId, string> | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingWarehouse, setPendingWarehouse] = useState<WarehousePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const warehouseQuery = useQuery({
    queryKey,
    queryFn: () => api.siteWarehouse(hostName, site.site_name),
  });

  const startEdit = async () => {
    setError(null);
    setSaved(false);
    setEditLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => api.siteWarehouse(hostName, site.site_name),
      });
      setDrafts(draftsFromWarehouse(data.warehouse));
      setEditing(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warehouse configuration');
    } finally {
      setEditLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setConfirmOpen(false);
    setPendingWarehouse(null);
    setError(null);
    setDrafts(null);
  };

  const requestSave = () => {
    if (!drafts) {
      return;
    }
    const parsed = parseWarehouseDrafts(drafts);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    setPendingWarehouse(parsed.warehouse);
    setConfirmOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: (warehouse: WarehousePayload) => api.saveSiteWarehouse(hostName, site.site_name, warehouse),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      setConfirmOpen(false);
      setPendingWarehouse(null);
      setEditing(false);
      setDrafts(null);
      setError(null);
      setSaved(true);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const warehouse = warehouseQuery.data?.warehouse;
  const active = WAREHOUSE_TABS.find(tab => tab.id === activeTab) ?? WAREHOUSE_TABS[0];
  const displayedValue =
    editing && drafts ? drafts[active.id] : JSON.stringify(sectionValue(warehouse, active), null, 2);

  return (
    <Box borderWidth='1px' borderColor='card.border' borderRadius='md' p={3}>
      <Flex align='center' gap={3} wrap='wrap'>
        <Text fontWeight='bold' flex={1}>
          {site.site_name}
        </Text>
        {!editing ? (
          <Button size='xs' variant='outline' onClick={() => void startEdit()} loading={editLoading}>
            Edit
          </Button>
        ) : (
          <>
            <Button size='xs' variant='outline' onClick={cancelEdit} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button size='xs' colorPalette='primary' onClick={requestSave} disabled={saveMutation.isPending}>
              Save
            </Button>
          </>
        )}
      </Flex>
      {saved && !editing ? (
        <Text fontSize='sm' color='green.600' mt={2}>
          Warehouse configuration saved.
        </Text>
      ) : null}
      {error ? (
        <Text fontSize='sm' color='red.500' mt={2}>
          {error}
        </Text>
      ) : null}
      {warehouseQuery.isLoading && !editing ? (
        <Flex mt={2} justify='center'>
          <Spinner size='sm' />
        </Flex>
      ) : warehouseQuery.isError && !editing ? (
        <Text fontSize='sm' color='red.500' mt={2}>
          {(warehouseQuery.error as Error).message}
        </Text>
      ) : (
        <Box mt={3}>
          <Flex className='warehouse-tabs' gap={1} wrap='wrap'>
            {WAREHOUSE_TABS.map(tab => {
              const count = sectionCount(displayedSection(tab, drafts, warehouse), tab.kind);
              return (
                <Button
                  key={tab.id}
                  size='xs'
                  variant={activeTab === tab.id ? 'solid' : 'outline'}
                  colorPalette={activeTab === tab.id ? 'primary' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label} ({count})
                </Button>
              );
            })}
          </Flex>
          <Suspense
            fallback={
              <Flex mt={2} h='420px' align='center' justify='center'>
                <Spinner size='sm' />
              </Flex>
            }
          >
            <JsonFoldingEditor
              value={displayedValue}
              readOnly={!editing}
              onChange={
                editing
                  ? next => {
                      if (!drafts) {
                        return;
                      }
                      setDrafts({ ...drafts, [active.id]: next });
                      setSaved(false);
                    }
                  : undefined
              }
            />
          </Suspense>
        </Box>
      )}

      {confirmOpen ? (
        <Dialog.Root
          open
          onOpenChange={event => (!event.open && !saveMutation.isPending ? setConfirmOpen(false) : undefined)}
        >
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content maxW='md'>
                <Dialog.Header>
                  <Dialog.Title>Confirm warehouse save</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Text>
                    This will save site settings, container types, layers, stations, baskets, robots, rails, racklights,
                    locations, and carts for site <b>{site.site_name}</b> on host <b>{hostName}</b> through that
                    host&apos;s data-portal. Non-empty entity lists replace that type for the site. This cannot be
                    undone from site-manager.
                  </Text>
                  {error ? (
                    <Text color='red.500' mt={3}>
                      {error}
                    </Text>
                  ) : null}
                </Dialog.Body>
                <Dialog.Footer gap={2}>
                  <Button variant='outline' onClick={() => setConfirmOpen(false)} disabled={saveMutation.isPending}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette='primary'
                    loading={saveMutation.isPending}
                    onClick={() => {
                      if (pendingWarehouse) {
                        saveMutation.mutate(pendingWarehouse);
                      }
                    }}
                  >
                    Confirm save
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      ) : null}
    </Box>
  );
}
